import { useState } from "react";
import ManagerLayout from "@/components/layout/ManagerLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  RefreshCw,
  ArrowRight,
  Check,
  X,
  AlertTriangle,
  ExternalLink,
  ChevronsUpDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AreaSelector } from "@/components/ui/area-selector";
import "@/components/ui/area-selector"; // Force re-import in case of caching issues
import { DUBAI_AREAS, AREA_CODE_MAP } from '@shared/areas';
import { useAuth } from "@/hooks/use-auth";

type AirbnbListing = {
  id: string;
  title: string;
  description: string;
  imageUrls: string[];
  location: string;
  area?: string;
  propertyCode?: string;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  listingUrl: string;
  airbnbListingId?: string;
  airbnbTitle?: string;
  features?: string[];
  lat?: number | null;
  lng?: number | null;
  locationTitle?: string | null;
  locationDescription?: string | null;
  rating?: string | null;
  reviewCount?: number | null;
};

export default function ImportAirbnbUrlPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [listings, setListings] = useState<AirbnbListing[]>([]);
  const [selectedListings, setSelectedListings] = useState<Set<string>>(new Set());
  const [listingAreas, setListingAreas] = useState<Record<string, string>>({});
  const [fetchComplete, setFetchComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importedPropertyIds, setImportedPropertyIds] = useState<number[]>([]);
  const [selectedArea, setSelectedArea] = useState<string>("");
  
  // Area options for the dropdown - these should match the options in PropertyForm.tsx
  // Area options with codes that will be used in the property code (DUB-XX-0000)
  const areaOptions = [
    { value: "Downtown Dubai", label: "Downtown Dubai", code: "DO" }, // DO for Downtown
    { value: "Dubai Marina", label: "Dubai Marina", code: "MR" },
    { value: "Jumeirah Village Circle", label: "JVC", code: "JV" },
    { value: "Palm Jumeirah", label: "Palm Jumeirah", code: "PJ" },
    { value: "Jumeirah Beach Residence", label: "JBR", code: "JB" },
    { value: "Business Bay", label: "Business Bay", code: "BB" },
    { value: "Dubai Hills Estate", label: "Dubai Hills", code: "DH" },
    { value: "Al Barsha", label: "Al Barsha", code: "BA" },
    { value: "Deira", label: "Deira", code: "DE" },
    { value: "Mirdif", label: "Mirdif", code: "MI" },
    { value: "Dubai Creek Harbour", label: "Dubai Creek Harbour", code: "DC" },
    { value: "Dubai Land", label: "Dubai Land", code: "DL" },
    { value: "DIFC", label: "DIFC", code: "DF" },
    { value: "Umm Suqeim", label: "Umm Suqeim", code: "US" },
    { value: "Other", label: "Other", code: "OT" }
  ];

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) {
      toast({
        title: "Missing Information",
        description: "Please enter an Airbnb listing URL",
        variant: "destructive",
      });
      return;
    }

    // Validate the URL - check if it's an Airbnb URL (all domains)
    if (!url.includes("airbnb.")) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid Airbnb listing URL",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setFetchError(null);
    setListings([]);
    setSelectedListings(new Set());
    setFetchComplete(false);
    setImportComplete(false);
    // setSelectedArea("");

    try {
      console.log("Fetching Airbnb listing from URL:", url);

      // Important: We need to use the correct API endpoint for the direct URL scraping
      const response = await apiRequest("POST", "properties/get-properties-by-url", {
        url,
      });

      // Check if the response is valid JSON
      // const contentType = response.headers.get("content-type");
      // if (!contentType || !contentType.includes("application/json")) {
      //   throw new Error(
      //     `Server response is not in JSON format (${contentType || "unknown content type"})`,
      //   );
      // }

      const result =  response;
      // console.log("API response:", result);

      if (!result.listings || !Array.isArray(result.listings) || result.listings.length === 0) {
        setFetchError(result.message || "Failed to fetch Airbnb listings");
        toast({
          title: "Error Fetching Listings",
          description: result.message || "Failed to fetch Airbnb listings",
          variant: "destructive",
        });
      } else {
        console.log("Setting listings data:", result.listings);
        // Convert the API response format to our component's listing format
        const listingsData = result.listings.map((listing: any) => ({
          id: listing.id || `airbnb-${Date.now()}-${Math.random()}`,
          title: listing.title || "Airbnb Property",
          airbnbListingId: listing.id,
          airbnbTitle: listing.title,
          description: listing.description || "",
          imageUrls: listing.imageUrls || [],
          location: listing.location || "Dubai",
          area: listing.area || "",
          propertyCode: listing.propertyCode || "",
          bedrooms: listing.bedrooms ?? 1,
          bathrooms: listing.bathrooms ?? 1,
          maxGuests: listing.maxGuests ?? 2,
          listingUrl: listing.listingUrl || url,
          features: listing.features ||  ['Wi-Fi', 'Air Conditioning'],
          lat: listing.lat ?? null,
          lng: listing.lng ?? null,
          locationTitle: listing.locationTitle ?? null,
          locationDescription: listing.locationDescription ?? null,
          rating: listing.rating ?? null,
          reviewCount: listing.reviewCount ?? null,
        }));
        
        setListings(listingsData);
        setFetchComplete(true);
        // Select all by default
        setSelectedListings(new Set(listingsData.map((l: any) => l.id)));

        // Map detected area to areaOptions value (case-insensitive)
        const mappedAreas: Record<string, string> = {};
        listingsData.forEach((l: any) => {
          const found = areaOptions.find(opt => l.area && opt.value.toLowerCase() === l.area.toLowerCase());
          mappedAreas[l.id] = found ? found.value : "Other";
        });
        setListingAreas(mappedAreas);
        
        toast({
          title: "Listings Found",
          description: `Successfully fetched ${listingsData.length} properties`,
        });
      }
    } catch (error: any) {
      console.error("Error fetching Airbnb property:", error);

      // Special handling for property already exists error
      if (error) {
        console.log("error",error);
        toast({
          title: "Property Already Exists",
          description:  "This property has already been imported to StayMiddle.",
          variant: "destructive",
        });
        return;
      }

      // Special handling for HTML responses which might indicate Airbnb anti-scraping measures
      let errorMessage = error.message || "An unexpected error occurred";

      if (
        errorMessage.includes("<!DOCTYPE") ||
        errorMessage.includes("not in JSON format") ||
        errorMessage.includes("Unexpected token")
      ) {
        errorMessage =
          "Please try again later or with a different URL.";
      }

      setFetchError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAreaChange = (listingId: string, area: string) => {
    setListingAreas(prev => ({
      ...prev,
      [listingId]: area
    }));
  };

  // Import the properties
  const importProperties = async () => {
    if (listings.length === 0) {
      toast({
        title: "No Listings Selected",
        description: "No property listings to import",
        variant: "destructive",
      });
      return;
    }

    const listingsToImport = listings.filter(listing => selectedListings.has(listing.id));
    
    if (listingsToImport.length === 0) {
      toast({
        title: "No Listings Selected",
        description: "Please select at least one property to import",
        variant: "destructive",
      });
      return;
    }

    // Check if all selected listings have an area selected
    const missingAreas = listingsToImport.filter(listing => !listingAreas[listing.id]);
    if (missingAreas.length > 0) {
      toast({
        title: "Missing Area Selection",
        description: "Please select an area for all selected properties",
        variant: "destructive",
      });
      return;
    }

    setImportInProgress(true);
    setProgress(10);

    try {
      // Format the properties for import
      const propertiesToImport = listingsToImport.map(listing => {
        const selectedArea = listingAreas[listing.id];
        const selectedAreaObj = areaOptions.find(area => area.value === selectedArea);
        
        if (!selectedAreaObj || !selectedAreaObj.code) {
          throw new Error(`Invalid area selected for property: ${listing.title}`);
        }
        
        const areaCode = selectedAreaObj.code;
        
        // Generate a new property code if needed
        let propertyCode = listing.propertyCode;
        if (!propertyCode || !propertyCode.includes(`-${areaCode}-`)) {
          const propertyNumber = Math.floor(1000 + Math.random() * 9000);
          propertyCode = `DUB-${areaCode}-${propertyNumber}`;
        }

        return {
          id: listing.id,
          title: listing.title,
          airbnbListingId: listing.airbnbListingId,
          airbnbTitle: listing.airbnbTitle,
          description: listing.description,
          location: listing.location,
          area: selectedArea,
          areaCode: areaCode,
          propertyCode: propertyCode,
          bedrooms: listing.bedrooms,
          bathrooms: listing.bathrooms,
          maxGuests: listing.maxGuests,
          listingUrl: listing.listingUrl,
          imageUrls: listing.imageUrls,
          imageUrl: listing.imageUrls[0],
          features: listing.features,
          lat: listing.lat ?? null,
          lng: listing.lng ?? null,
          locationTitle: listing.locationTitle ?? null,
          locationDescription: listing.locationDescription ?? null,
          rating: listing.rating ?? null,
          reviewCount: listing.reviewCount ?? null,
          selected: true,
        };
      });

      setProgress(30);

      // Call the API to import the properties
      const response = await apiRequest(
        "POST",
        "properties/import-properties",
        {
          properties: propertiesToImport,
        },
      );

      setProgress(70);

      // if (!response.ok) {
        // const errorData = await response.json();
        // throw new Error(errorData.message || "Failed to import properties");
      // }

      const result =  response;

      setProgress(100);
      setImportComplete(true);

      // Store the imported property IDs
      if (result.results.success > 0 && result.results.details.length > 0) {
        const importedIds = result.results.details
          .filter((detail: any) => detail.status === "success")
          .map((detail: any) => detail.newPropertyId);
        setImportedPropertyIds(importedIds);
        // If only one property was imported, redirect to edit page
        if (importedIds.length === 1) {
          window.location.href = `/manager/edit-property/${importedIds[0]}`;
          return;
        }
      }

      toast({
        title: "Import Complete",
        description: result.message,
        variant: result.results.failed > 0 ? "destructive" : "default",
      });
    } catch (error: any) {
      console.error("Error importing properties:", error);
      toast({
        title: "Import Failed",
        description:
          error.message || "Failed to import properties. Please try again.",
        variant: "destructive",
      });
    } finally {
      setImportInProgress(false);
    }
  };

  const toggleListingSelection = (listingId: string) => {
    setSelectedListings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listingId)) {
        newSet.delete(listingId);
      } else {
        newSet.add(listingId);
      }
      return newSet;
    });
  };

  const selectAllListings = () => {
    setSelectedListings(new Set(listings.map(listing => listing.id)));
  };

  const deselectAllListings = () => {
    setSelectedListings(new Set());
  };

  return (
    <ManagerLayout>
      <div className="container py-8 max-w-3xl">
        {(user?.status === 'pending' || user?.status === 'inactive') && (
          <div className="mb-6 p-4 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded">
            <strong>Account Pending Approval:</strong> You cannot import properties until your account is approved by the admin.
          </div>
        )}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Import Airbnb Listing by URL
            </h1>
             
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Step 1: Enter Airbnb Listing URL</CardTitle>
              <CardDescription>
                Enter the URL of an Airbnb listing to import all its details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="flex gap-2 mb-6">
                <Input
                  type="text"
                  placeholder="Paste Airbnb listing URL here"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  className="flex-1"
                  disabled={user?.status === 'pending' || user?.status === 'inactive'}
                />
                <Button type="submit" disabled={isLoading || user?.status === 'pending' || user?.status === 'inactive'}>
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                  Fetch
                </Button>
              </form>
            </CardContent>
          </Card>

        

          {fetchComplete && listings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Step 2: Review Property Details</CardTitle>
                <CardDescription>
                  Review and select the properties you want to import
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-muted-foreground">
                    {selectedListings.size} of {listings.length} properties selected
                  </div>
                  {listings.length > 1 && (
                    <div className="space-x-2">
                      <Button variant="outline" size="sm" onClick={selectAllListings}>
                        Select All
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllListings}>
                        Deselect All
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  {listings.map((listing) => (
                    <Card key={listing.id} className={`relative ${selectedListings.has(listing.id) ? 'border-primary' : ''}`}>
                      <div className="absolute top-4 right-4">
                        {listings.length > 1 ? (
                          <Button
                            variant={selectedListings.has(listing.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleListingSelection(listing.id)}
                          >
                            {selectedListings.has(listing.id) ? (
                              <Check className="h-4 w-4 mr-2" />
                            ) : null}
                            {selectedListings.has(listing.id) ? "Selected" : "Select"}
                          </Button>
                        ) : (
                          <span></span>
                        )}
                      </div>
                      <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Property Images */}
                          <div className="w-full md:w-1/2">
                            <div className="aspect-video overflow-hidden rounded-lg">
                              {listing.imageUrls.length > 0 ? (
                                <img
                                  src={listing.imageUrls[0]}
                                  alt={listing.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-muted flex items-center justify-center">
                                  <p className="text-muted-foreground">
                                    No image available
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Thumbnail gallery */}
                            {listing.imageUrls.length > 1 && (
                              <div className="grid grid-cols-5 gap-2 mt-2">
                                {listing.imageUrls.slice(0, 5).map((img, index) => (
                                  <div
                                    key={index}
                                    className="aspect-square rounded-md overflow-hidden"
                                  >
                                    <img
                                      src={img}
                                      alt={`${listing.title} - Image ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Property Details */}
                          <div className="w-full md:w-1/2 space-y-4">
                            <div>
                              <h3 className="text-xl font-semibold">{listing.title}</h3>
                              <p className="text-muted-foreground">
                                {listing.location}
                              </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline" className="text-sm">
                                {listing.bedrooms === 0 ? "Studio" : `${listing.bedrooms} bedroom${listing.bedrooms !== 1 ? "s" : ""}`}
                              </Badge>
                              <Badge variant="outline" className="text-sm">
                                {listing.bathrooms} bathroom
                                {listing.bathrooms !== 1 ? "s" : ""}
                              </Badge>
                              <Badge variant="outline" className="text-sm">
                                {listing.maxGuests} guest
                                {listing.maxGuests !== 1 ? "s" : ""}
                              </Badge>
                            </div>

                            {/* Area Selection for this property */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label htmlFor={`property-area-${listing.id}`} className="text-sm font-medium">
                                  Property Area <span className="text-destructive">*</span>
                                </Label>
                                {selectedListings.has(listing.id) && !listingAreas[listing.id] && 
                                  <p className="text-sm text-destructive">Area selection is required</p>
                                }
                              </div>
                              
                              <div className="relative">
                                <select
                                  id={`property-area-${listing.id}`}
                                  value={listingAreas[listing.id] || ""}
                                  onChange={(e) => handleAreaChange(listing.id, e.target.value)}
                                  className="w-full px-3 py-2 border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary
                                    border-amber-300"
                                  aria-label="Select property area"
                                >
                                  <option value="" disabled>Select property area</option>
                                  {areaOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label} {option.code ? `(${option.code})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                </div>
                              </div>
                            </div>

                            <div className="max-h-48 overflow-y-auto text-sm">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: listing.description,
                                }}
                                className="prose prose-sm"
                              />
                            </div>

                            <div className="pt-2">
                              <a
                                href={listing.listingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary flex items-center"
                              >
                                View on Airbnb <ExternalLink className="h-4 w-4 ml-1" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="border-t p-6">
                <div className="w-full">
                  {importComplete ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 text-green-700 p-4 rounded-md flex items-start">
                        <Check className="h-5 w-5 mr-2 mt-0.5" />
                        <div>
                          <p className="font-semibold">Import completed!</p>
                          <p>
                            {importedPropertyIds.length} properties have been successfully imported to your account.
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => {
                            setUrl("");
                            setListings([]);
                            setSelectedListings(new Set());
                            setFetchComplete(false);
                            setImportComplete(false);
                          }}
                        >
                          Import More Properties
                        </Button>
                        {importedPropertyIds.length > 0 && (
                          <Button
                            onClick={() => {
                              window.location.href = `/manager/properties`;
                            }}
                          >
                            View Imported Properties
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : importInProgress ? (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span>Importing properties...</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="text-sm">
                        {selectedListings.size === 0 ? (
                          <span className="text-amber-600 font-medium flex items-center">
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            Please select at least one property to import
                          </span>
                        ) : (
                          <span className="text-green-600 font-medium flex items-center">
                            <Check className="h-4 w-4 mr-1" />
                            {selectedListings.size} properties selected for import
                          </span>
                        )}
                      </div>
                      <Button 
                        onClick={importProperties} 
                        disabled={selectedListings.size === 0}
                        className={selectedListings.size === 0 ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        Import Selected Properties
                      </Button>
                    </div>
                  )}
                </div>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>
    </ManagerLayout>
  );
}
